/**
 * PRODGY WORKFLOW EDITOR
 *
 * Monaco JSON editor for prodgy (new engine) executor agents. Shown to
 * superadmins instead of the ReactFlow visual editor. Loads the main
 * workflow_base.settings, lets the user edit it as raw JSON, and can save
 * (patch workflow_base.settings) or publish (create a new agent_version row
 * via POST /api/agent-base/:id/prodgy-publish).
 */

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Editor from "@monaco-editor/react";
import { Loader2, Save, Upload, AlertCircle } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/components/ui/use-toast";
import { useTheme } from "@/shared/hooks/use-theme";
import { WorkflowBaseService } from "@/features/Workflows/services/WorkflowBaseService";
import { post, put } from "@/shared/utils/fetchApi";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import type { AgentBase } from "@/features/Agents/types/AgentBaseInterface";

interface Props {
  agent: AgentBase;
  onPublished?: () => void;
}

interface WorkflowBaseRecord {
  id: string;
  name?: string;
  settings?: Record<string, unknown> | null;
}

const isUninstaller = (wb: WorkflowBaseRecord): boolean =>
  !!(wb.name && wb.name.toLowerCase().includes("uninstall"));

export function ProdgyWorkflowEditor({ agent, onPublished }: Props) {
  const { toast } = useToast();
  const { theme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [workflowBaseId, setWorkflowBaseId] = useState<string | null>(null);
  const [originalJson, setOriginalJson] = useState<string>("");
  const [currentJson, setCurrentJson] = useState<string>("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadWorkflow = useCallback(async () => {
    setLoading(true);
    setParseError(null);
    try {
      const response = await WorkflowBaseService.getByAgentBaseId(agent.id);
      const workflows = (
        Array.isArray(response) ? response : []
      ) as WorkflowBaseRecord[];
      if (workflows.length === 0) {
        toast({
          variant: "destructive",
          title: "Workflow não encontrado",
          description: `Nenhum workflow_base registrado para o agent_base ${agent.id}.`,
        });
        return;
      }
      // Prefer a row whose name does not look like an uninstaller; otherwise
      // fall back to the first row so single-workflow agents still load.
      const mainWf = workflows.find((wb) => !isUninstaller(wb)) || workflows[0];
      if (!mountedRef.current) return;
      setWorkflowBaseId(mainWf.id);
      const pretty = JSON.stringify(mainWf.settings || {}, null, 2);
      setOriginalJson(pretty);
      setCurrentJson(pretty);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar workflow",
        description: resolveApiErrorMessage(error),
      });
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [agent.id, toast]);

  useEffect(() => {
    loadWorkflow();
  }, [loadWorkflow]);

  const isDirty = useMemo(
    () => currentJson !== originalJson,
    [currentJson, originalJson],
  );

  const parseCurrent = useCallback((): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(currentJson);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setParseError("O workflow_schema precisa ser um objeto JSON.");
        return null;
      }
      setParseError(null);
      return parsed as Record<string, unknown>;
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, [currentJson]);

  const handleSave = useCallback(async () => {
    const parsed = parseCurrent();
    if (!parsed || !workflowBaseId) return;
    setSaving(true);
    try {
      // v2: gêmea é PUT /workflow-bases/:id (plural, superadmin — igual legacy)

      await put(`/workflow-bases/${workflowBaseId}`, { settings: parsed });
      setOriginalJson(currentJson);
      toast({
        title: "Workflow salvo",
        description: "Alterações persistidas em workflow_base.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar",
        description: resolveApiErrorMessage(error),
      });
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [currentJson, parseCurrent, toast, workflowBaseId]);

  const handlePublish = useCallback(async () => {
    const parsed = parseCurrent();
    if (!parsed) return;
    if (!Array.isArray((parsed as Record<string, unknown>).nodes)) {
      setParseError("workflow_schema.nodes deve ser um array.");
      return;
    }
    setPublishing(true);
    try {
      // path plural + response flat (sem envelope {success, data}) — D2-B/D3-B
      const response = await post<{ version?: string }>(
        `/agent-bases/${agent.id}/prodgy-publish`,
        { workflow_schema: parsed },
      );
      const version = response?.version;
      setOriginalJson(currentJson);
      toast({
        title: "Versão publicada",
        description: version
          ? `Nova versão ${version} criada.`
          : "Nova versão criada.",
      });
      onPublished?.();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao publicar",
        description: resolveApiErrorMessage(error),
      });
    } finally {
      if (mountedRef.current) setPublishing(false);
    }
  }, [agent.id, currentJson, onPublished, parseCurrent, toast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Workflow Schema (prodgy)</h3>
          <p className="text-sm text-muted-foreground">
            Editor JSON do workflow_base.settings — para agentes com
            executor=prodgy (engine novo).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty || saving || publishing}
            onClick={handleSave}
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Salvar
          </Button>
          <Button
            size="sm"
            disabled={!isDirty || publishing || saving}
            onClick={handlePublish}
          >
            {publishing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            Publicar
          </Button>
        </div>
      </div>

      {parseError && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span className="break-all">{parseError}</span>
        </div>
      )}

      <div
        className="overflow-hidden rounded-md border"
        style={{ height: "70vh" }}
      >
        <Editor
          height="100%"
          language="json"
          value={currentJson}
          onChange={(value) => {
            setCurrentJson(value || "");
            if (parseError) setParseError(null);
          }}
          theme={theme === "dark" ? "vs-dark" : "vs"}
          options={{
            automaticLayout: true,
            minimap: { enabled: false },
            wordWrap: "on",
            formatOnPaste: true,
            tabSize: 2,
            scrollBeyondLastLine: false,
            fontSize: 13,
          }}
        />
      </div>
    </div>
  );
}
